import type { ArtworkRecord } from '../../contracts/src/search.js';
import { ArtEmbeddingSpace } from './art-embedding-space.js';

export interface VisualEnrichmentProgress {
  readonly completed: number;
  readonly total: number;
  readonly failures: number;
}

export class VisualIndexEnricher {
  readonly #space: ArtEmbeddingSpace;
  readonly #concurrency: number;

  constructor(space: ArtEmbeddingSpace, concurrency = 4) {
    this.#space = space;
    this.#concurrency = Math.max(1, concurrency);
  }

  async enrich(
    items: readonly ArtworkRecord[],
    onProgress: (progress: VisualEnrichmentProgress) => void
  ): Promise<readonly ArtworkRecord[]> {
    const enriched = [...items];
    let cursor = 0;
    let completed = 0;
    let failures = 0;

    const worker = async () => {
      while (cursor < items.length) {
        const index = cursor++;
        const item = items[index];
        if (!item) continue;
        try {
          const visual = item.visualVector?.length
            ? Float32Array.from(item.visualVector)
            : await this.#space.describeArtworkImage(item.thumbnailUrl ?? item.imageUrl);
          enriched[index] = { ...item, visualVector: [...visual], vector: [...this.#space.encodeArtwork(item, visual)] };
        } catch {
          failures += 1;
        } finally {
          completed += 1;
          onProgress({ completed, total: items.length, failures });
          await yieldToBrowser();
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(this.#concurrency, items.length) }, worker));
    return enriched;
  }
}

function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => resolve(), { timeout: 40 });
    } else {
      globalThis.setTimeout(resolve, 0);
    }
  });
}
